/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from '@components/atoms/ui-kit/button.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import CreateCustomerWizard from '@components/back-office/customer/create-customer-wizard';

import DataGridV2, {
  DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetCustomersBo } from '@utils/api/back-office/customer-bo/use-get-customers-bo.tsx';
import { useSearchResellersBo } from '@utils/api/back-office/customer-bo/use-search-resellers-bo';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { PATHS } from '../../router/paths.ts';
import { formatDate } from '@utils/date.ts';

import {
  CustomerBo as CustomerBo2,
  CustomerType
} from '@utils/types/back-office/customers';
import { Eye, Plus, Trash2, Flame, ImageIcon } from 'lucide-react';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Badge, StatusBadge } from '@components/atoms/ui-kit/badge';
import { useDeleteCustomerBo } from '@utils/api/back-office/customer-bo/use-delete-customer-bo';
import { usePurgeCustomerBo } from '@utils/api/back-office/customer-bo/use-purge-customer-bo';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';

type Row = {
  id: string;
  name: string;
  email: string;
  reseller: CustomerBo2['reseller'];
  parent_organization_id?: number | null;
  customer_type: CustomerType;
  registration_date: string;
  is_active?: boolean;
  is_reseller?: boolean;
  country?: string | null;
  logo?: string | null;
  invoice_currency?: 'EUR' | 'USD';
};

// Helper to derive customer type from existing fields if not provided by API
const deriveCustomerType = (customer: CustomerBo2): CustomerType => {
  if (customer.customer_type) return customer.customer_type;
  if (customer.is_reseller) return 'reseller';
  if (customer.reseller) return 'sub_org';
  return 'direct';
};

export default function CustomersBo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Server-driven list, controlled via URL params by DataGridV2
  const { data: customers, isLoading } = useGetCustomersBo();
  const { data: supportedCountries } = useGetModels();
  const { data: resellersData } = useSearchResellersBo();

  // Create a lookup map from reseller name to reseller ID
  const resellerNameToId = useMemo(() => {
    const map = new Map<string, number>();
    if (resellersData?.result) {
      resellersData.result.forEach(reseller => {
        map.set(reseller.name, Number(reseller.id));
      });
    }
    return map;
  }, [resellersData]);

  const { mutateAsync: deleteCustomer } = useDeleteCustomerBo();
  const { mutateAsync: purgeCustomer } = usePurgeCustomerBo();

  const handleRowClick = (row: Row) => {
    navigate(PATHS(row.id.toString()).CUSTOMER_CONTENT);
  };

  // Actions: view, soft-delete, hard-delete (3 items → auto 3-dot menu)
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => {
          navigate(PATHS(row.id.toString()).CUSTOMER_CONTENT);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        requiresConfirm: () => ({
          title: t('delete-confirmation'),
          body: t('are-you-sure-you-want-to-delete-this-customer'),
          confirmText: t('delete')
        }),
        run: async (row: Row) => {
          try {
            await deleteCustomer(Number(row.id));
            toast.success(t('customer-deleted-successfully'));
          } catch (e) {
            const err = e as AxiosError<BaseError>;
            toast.error(
              err?.response?.data?.message ?? t('something-went-wrong')
            );
          }
        }
      },
      {
        id: 'purge',
        label: t('delete-permanently'),
        icon: <Flame className="h-4 w-4" />,
        intent: 'danger',
        requiresConfirm: () => ({
          title: t('delete-permanently-confirmation'),
          body: t('are-you-sure-you-want-to-permanently-delete-this-customer'),
          confirmText: t('delete-permanently')
        }),
        run: async (row: Row) => {
          try {
            const status = await purgeCustomer(Number(row.id));
            toast.success(
              status === 202
                ? t('customer-purge-in-progress')
                : t('customer-purged-successfully')
            );
          } catch (e) {
            const err = e as AxiosError<BaseError>;
            toast.error(
              err?.response?.data?.error_description ??
                err?.response?.data?.message ??
                err?.response?.data?.detail ??
                t('something-went-wrong')
            );
          }
        }
      }
    ],
    [t, navigate, deleteCustomer, purgeCustomer]
  );

  const rows = useMemo<Row[]>(() => {
    if (!customers?.result || !Array.isArray(customers.result)) return [];

    return customers.result.map((customer: CustomerBo2) => {
      const countryKey = customer.country?.toLowerCase();
      const countryData = supportedCountries?.result.find(
        c => c.key === countryKey
      );
      const fullCountryName = countryData ? countryData.name : customer.country;

      // Derive customer type
      const customerType = deriveCustomerType(customer);

      return {
        id: String(customer.id),
        name: customer.name,
        email: customer.email?.toLowerCase?.() ?? customer.email,
        reseller: customer.reseller,
        parent_organization_id: customer.parent_organization_id,
        customer_type: customerType,
        registration_date: formatDate(customer.registration_date || ''),
        is_active: customer.is_active,
        is_reseller: customer.is_reseller,
        country: fullCountryName ?? null,
        logo: customer.logo ?? null,
        invoice_currency: customer.invoice_currency
      };
    });
  }, [customers, supportedCountries]);

  // Map UI columns to API fields via filterKey; add server operators for selects/booleans
  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: '',
        field: 'logo',
        minWidth: 56,
        maxWidth: 56,
        width: 56,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const logo = getValue() as string | null;
          return (
            <div
              className="flex items-center justify-center w-10 h-7 rounded-2xl overflow-hidden"
              style={{
                backgroundColor: logo ? 'transparent' : getColour('appSoft')
              }}
            >
              {logo ? (
                <img
                  src={logo}
                  alt=""
                  className="w-full h-full object-contain"
                />
              ) : (
                <ImageIcon
                  className="h-3.5 w-3.5 opacity-30"
                  style={{ color: getColour('icon') }}
                />
              )}
            </div>
          );
        }
      },
      {
        header: t('id'),
        field: 'id',
        filterKey: 'id',
        filterVariant: 'number',
        serverOperator: 'eq',
        minWidth: 90,
        maxWidth: 100,
        width: 100,
        enableSorting: true,
        enableFiltering: true,
        cell: ({ getValue }) => String(getValue() ?? ''),
        normalizeFilterValue: (val: string) => {
          // Only return value if it's a complete number
          // This prevents searching for partial IDs since backend only supports exact match
          const trimmed = val.trim();
          return trimmed.length > 0 && !isNaN(Number(trimmed)) ? trimmed : '';
        }
      },
      {
        header: t('name'),
        field: 'name',
        filterKey: 'name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text', // default op: ilike
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('type'),
        field: 'customer_type',
        filterKey: 'customer_type',
        minWidth: 100,
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('direct'), value: 'direct' },
          { label: t('reseller'), value: 'reseller' },
          { label: t('sub-organization'), value: 'sub_org' }
        ],
        cell: ({ getValue }) => {
          const type = getValue() as CustomerType;
          return (
            <StatusBadge
              status={type}
              label={t(type === 'sub_org' ? 'sub-org' : type)}
            />
          );
        }
      },
      {
        header: t('parent-reseller'),
        field: 'reseller',
        filterKey: 'reseller',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue, row }) => {
          const resellerName = getValue() as Row['reseller'];

          // No parent reseller
          if (!resellerName) {
            return <span style={{ color: getColour('inactiveText') }}>—</span>;
          }

          // Try to get parent ID from row data first, then from lookup map
          const parentId =
            row.original.parent_organization_id ??
            resellerNameToId.get(resellerName);

          // Has parent reseller with ID - make it clickable
          if (parentId != null) {
            return (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigate(PATHS(String(parentId)).CUSTOMER_CONTENT);
                }}
                className="text-left hover:underline cursor-pointer"
                style={{ color: getColour('activeText') }}
              >
                {String(resellerName)}
              </button>
            );
          }

          // Has reseller name but couldn't find ID (fallback display)
          return (
            <span style={{ color: getColour('normalText') }}>
              {String(resellerName)}
            </span>
          );
        }
      },
      {
        header: t('email'),
        field: 'email',
        filterKey: 'email',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('registered'),
        field: 'registration_date',
        filterKey: 'registration_date',
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date', // default op for dates: eq (handled in grid)
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('country'),
        field: 'country',
        filterKey: 'country',
        maxWidth: 150,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('status'),
        field: 'is_active',
        filterKey: 'is_active',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('active'), value: 'true' },
          { label: t('inactive'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const active = Boolean(getValue());
          return (
            <StatusBadge
              status={active ? 'active' : 'inactive'}
              label={active ? t('active') : t('inactive')}
            />
          );
        }
      },
      {
        header: t('currency'),
        field: 'invoice_currency',
        maxWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const currency = (getValue() as 'EUR' | 'USD' | undefined) ?? 'EUR';
          return (
            <Badge color={currency === 'USD' ? 'emerald' : 'blue'}>
              {currency === 'USD' ? '$ USD' : '€ EUR'}
            </Badge>
          );
        }
      }
    ];
  }, [t, getColour, navigate, resellerNameToId]);

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col pt-2">
      <CreateCustomerWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
      />

      <PageHeader
        title={t('customers')}
        caption={t(
          'list-of-all-the-customers-including-their-name-contact-and-actions'
        )}
      >
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setIsWizardOpen(true)}
          label={t('new-customer')}
        />
      </PageHeader>

      <div className="w-full pt-4 flex-1 min-h-0 flex flex-col">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enablePagination
          manualPagination
          totalItems={customers?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          enableRowSelection={false}
          striped
          dense
          grid={false}
          fill
          loading={isLoading}
          onRowClick={handleRowClick}
          onCellClick={row => handleRowClick(row)}
          getRowId={row => String(row.id)}
        />
      </div>
    </div>
  );
}
